import { AssigneeDropdown } from "@/components/dashboard/AssigneeDropdown";
import { DashboardTable } from "@/components/dashboard/dashboard-table";
import { FilterTab } from "@/components/filter/FilterTab";
import { RebuildHeader } from "@/components/layout/RebuildHeader";
import { SearchInput } from "./SearchInput";
import { Button } from "@/components/ui/button";
import { CreateEstimationContext } from "@/context/create-estimation-context";
import { useContext } from "react";
import { useDashboard } from "./hooks/useDashboard";
import { useStatusOptions } from "./hooks/useStatusOptions";
import { useIsMobile } from "@/hooks/useIsMobile";
import { MobileDashboardPage } from "./MobileDashboardPage";
import { NavUserDropdown } from "@/components/layout/nav-user-dropdown/NavUserDropdown";
import { useAnalytics } from "@/analytics";
import { Plus } from "lucide-react";

export const DashboardPage = () => {
  const context = useContext(CreateEstimationContext);
  const statusOptions = useStatusOptions();
  const isMobile = useIsMobile();
  const { trackEvent, ANALYTICS_EVENTS } = useAnalytics();

  const {
    isLoading,
    onClickRow,
    itemPerPage,
    estimations,
    currentAssignee,
    currentProjectName,
    currentSearchStatus,
    searchByUser,
    searchByStatus,
    searchByProject,
    tableMeta,
    page,
    setPage,
    total,
    setItemPerPage,
  } = useDashboard();

  // need to do this since the Estimate-upload page is a pop-up that hovers over this component and
  // therefore takes the size of this component, so we need to make sure that this component doesn't
  // overflow and go outside of the screen
  if (isMobile) {
    return <MobileDashboardPage />;
  }

  return (
    <div className="relative flex h-full w-full min-w-0 flex-col">
      <RebuildHeader
        rightSection={
          <div className="flex flex-row items-center space-x-2">
            <div data-testid="estimation-header-search-section">
              <SearchInput value={currentProjectName} onSearch={searchByProject} />
            </div>
            <NavUserDropdown />
          </div>
        }
      />
      <FilterTab
        selectedOption={{
          value: currentSearchStatus?.length ? currentSearchStatus[0] : "",
          id: currentSearchStatus?.length ? currentSearchStatus[0] : "",
        }}
        className="space-x-2"
        options={statusOptions}
        onSelectOption={(option) => searchByStatus([option.value])}
        rightSection={
          <div data-testid="estimation-filter-right-section" className="flex flex-row items-center space-x-2">
            <AssigneeDropdown
              onSelect={searchByUser}
              onAllSelect={() => {
                searchByUser(undefined);
              }}
              value={currentAssignee}
            />
            <Button
              onClick={() => {
                trackEvent(ANALYTICS_EVENTS.ESTIMATE_CREATION_STARTED, {});
                context?.toggleShowNewEstimationModal();
              }}
              data-testid="new-estimate-button"
              variant="accent"
            >
              <Plus className="h-4 w-4" />
              New estimate
            </Button>
          </div>
        }
      />

      <div data-testid="estimation-table" className="flex-grow overflow-auto">
        <DashboardTable
          data={estimations}
          isLoading={isLoading}
          onClickRow={onClickRow}
          pageSize={itemPerPage}
          meta={tableMeta}
          page={page}
          total={total}
          onPageChange={setPage}
          onPageSizeChange={setItemPerPage}
        />
      </div>
    </div>
  );
};
